import type { SupportTicketDetail } from './support-api';

/**
 * Reading the post-resolution review invitation off a ticket detail response.
 *
 * Pure and free of React so both branches are unit-testable: the invitation's whole point is
 * that nobody is looking when it fires, so the panel is the only account of it an agent ever
 * gets and a wrong state here is indistinguishable from a broken timer.
 *
 * The API side of the same lifecycle is `SupportReviewAskService`; see
 * docs/architecture/support-desk.md § _Making a three-day timer visible_.
 */

/** The prefix every review-invitation history entry's `SupportTicketEventType` carries. */
const REVIEW_ASK_EVENT_PREFIX = 'REVIEW_ASK_';

/** One history entry, as the generated detail model returns it. */
type TicketEvent = SupportTicketDetail['events'][number];

/** Where the invitation stands, as an i18n leaf plus the timestamp that leaf interpolates. */
export interface ReviewAskState {
  /** Full i18n key. */
  readonly key: string;
  /** ISO timestamp for the `time` placeholder, or null for a leaf that takes none. */
  readonly time: string | null;
}

/** Whether a history entry belongs to the review invitation's lifecycle. */
export function isReviewAskEvent(event: TicketEvent): boolean {
  // `type` is optional on the generated model even though the API always sends it, which is
  // why this asks the optional-chained question rather than trusting the shape.
  return event.type?.startsWith(REVIEW_ASK_EVENT_PREFIX) ?? false;
}

/**
 * The trailing detail of one history entry.
 *
 * Most entries are a transition and read `FROM → TO`, both sides translated out of the enum
 * they belong to. The review-invitation entries are not: they carry no `fromValue` at all, so a
 * `FROM → TO` rendering prints `— → …`. Those read as a single value instead.
 *
 * Which value depends on the entry, and this is the only branch that knows: `REVIEW_ASK_SENT`
 * carries nothing, `REVIEW_ASK_SCHEDULED` carries an ISO due date that has to be localised as a
 * DATE, and `REVIEW_ASK_CANCELLED` / `_SKIPPED` carry a reason token that goes through the same
 * translator as every other value these screens show — `supportEventValueGroup` maps those two
 * onto `reviewAskReason` so `formatSide` can label them.
 *
 * Both formatters are injected so this stays a pure function of its arguments and needs no i18n
 * wiring: what it owns is the choice BETWEEN the shapes, not the copy in any of them.
 *
 * @param event - The event as the API returns it.
 * @param formatDate - Localising date formatter.
 * @param formatSide - Localises one value out of the enum its event belongs to, and supplies
 *   the placeholder for a side the entry does not carry.
 * @returns The detail string, or null when the entry carries none.
 */
export function historyDetail(
  event: TicketEvent,
  formatDate: (iso: string) => string,
  formatSide: (value: string | null | undefined) => string,
): string | null {
  if (isReviewAskEvent(event)) {
    if (!event.toValue) return null;
    // A due date, or a reason like `reopened-open` / `asked-recently`. Only the first parses,
    // which is what decides between the two — the entry type would work as well, but this
    // survives a fifth entry kind being added without teaching this branch about it.
    const asDate = new Date(event.toValue);
    return Number.isNaN(asDate.getTime())
      ? formatSide(event.toValue)
      : formatDate(event.toValue);
  }
  if (!event.fromValue && !event.toValue) return null;
  return `${formatSide(event.fromValue)} → ${formatSide(event.toValue)}`;
}

/**
 * The i18n leaf and timestamp describing where the review invitation stands.
 *
 * Four states, because "the agent ticked a box" and "a mail is on a timer" are not the same
 * fact and the difference is exactly what an agent comes back to this page to check:
 *
 * - `reviewAskPending` — queued, `time` is when it fires.
 * - `reviewAskSent` — delivered, `time` is when.
 * - `reviewAskStopped` — there WAS an invitation and nothing is pending any more: cancelled by
 *   a reopen, refused by the gate, its send failed for good, or it never queued because Redis
 *   was down. The history entries beside it carry which.
 * - `null` — nobody ever asked; the panel row is omitted entirely rather than reading "no".
 *
 * The stopped state cannot be read off `requested` alone. A cancel deliberately clears
 * `reviewAskRequested` — that column is the worker gate's answer to "does anyone still want
 * this sent", and after a reopen the answer is no — which would leave the commonest cancel of
 * all showing no row at all. So the history is the second witness: a `REVIEW_ASK_*` entry is
 * durable proof an invitation existed, whatever the columns now say.
 *
 * @param reviewAsk - The detail response's `reviewAsk` block.
 * @param events - The ticket's history, for the durable proof described above.
 */
export function reviewAskState(
  reviewAsk: SupportTicketDetail['reviewAsk'],
  events: ReadonlyArray<TicketEvent>,
): ReviewAskState | null {
  if (reviewAsk.sentAt) {
    return { key: 'support.ticket.reviewAskSent', time: reviewAsk.sentAt };
  }
  if (reviewAsk.scheduledAt) {
    return {
      key: 'support.ticket.reviewAskPending',
      time: reviewAsk.scheduledAt,
    };
  }
  if (reviewAsk.requested || events.some(isReviewAskEvent)) {
    return { key: 'support.ticket.reviewAskStopped', time: null };
  }
  return null;
}
